import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import Page from '../../components/Page';
import Breadcrumb from '../../components/Breadcrumb';
import client from '../../lib/feathers';

/**
 * The aggregate view: how much, and is it going up.
 *
 * The visits log answers "what did this person do". This answers the other
 * question, and the two are not substitutes. That split is most of what was
 * thin about GA, which was strong here and had almost nothing to say about a
 * single journey.
 *
 * EVERY DEFINITION COMES FROM THE API
 *
 * The labels and the caveats below are not written here. They arrive with the
 * numbers from `admin/analytics-summary`, so this page cannot show a figure
 * under a label the server does not agree with, and cannot show it without
 * the sentence saying what it excludes. See api/src/lib/analytics-metrics.
 *
 * IMPRESSIONS SIT APART ON PURPOSE
 *
 * Listings being shown is the biggest number by an order of magnitude and the
 * one least about people. It is rendered separately, never in the default
 * chart, and never added into a total that claims to describe what anybody
 * did.
 */

/**
 * `unknown` is offered rather than hidden.
 *
 * A row whose origin the server did not recognise has no surface, and asking
 * for app or web excludes it without saying so. Making the pile selectable is
 * what turns "there is no app traffic" into "there are N rows we could not
 * attribute", which are very different findings.
 */
const SURFACES = [
  { key: 'all', label: 'Everything' },
  { key: 'app', label: 'App' },
  { key: 'web', label: 'Web' },
  { key: 'unknown', label: 'Unattributed' },
];

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
];

/** The people-facing metrics, in the order they are worth reading. */
const HEADLINE = ['visitors', 'sessions', 'views', 'interactions', 'outbound'];

const SERIES_COLORS = {
  visitors: '#7c3aed',
  sessions: '#2563eb',
  views: '#0891b2',
  interactions: '#16a34a',
  outbound: '#ea580c',
  impressions: '#9ca3af',
};

const number = (n) => (typeof n === 'number' ? n.toLocaleString() : '-');

/**
 * Better or worse than the period before.
 *
 * Renders nothing when the API sends null, which is what it sends when the
 * previous period was zero. Growth from nothing is not a percentage, and a
 * "+100%" there would be the same figure whether one thing happened or a
 * thousand.
 *
 * Direction is never coloured on its own. A fall in "Listings shown" is not a
 * failure and a rise is not an achievement, so only the metrics that describe
 * what people did get green and red.
 */
function Change({ value, coloured }) {
  if (value === null || value === undefined) return null;
  const up = value > 0;
  const flat = value === 0;
  const tone = !coloured || flat
    ? 'text-gray-500'
    : (up ? 'text-green-700' : 'text-red-700');
  return (
    <span className={`text-xs font-medium ${tone}`}>
      {up ? '+' : ''}{value}%
    </span>
  );
}

function MetricCard({
  metricKey, meta, value, change, selected, onToggle,
}) {
  if (!meta) return null;
  return (
    <button
      type="button"
      onClick={() => onToggle(metricKey)}
      // The caveat is on the card itself, not only in the panel below, so it
      // is present at the moment somebody reads the figure.
      title={`${meta.means}\n\n${meta.caveat}`}
      className={`text-left rounded-lg border p-4 transition ${
        selected
          ? 'border-gray-900 bg-white shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-400'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: SERIES_COLORS[metricKey] || '#9ca3af' }}
        />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {meta.label}
        </span>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-gray-900">{number(value)}</span>
        <Change value={change} coloured />
      </div>
      <div className="mt-1 text-xs leading-snug text-gray-600">{meta.means}</div>
    </button>
  );
}

export default function AnalyticsOverview() {
  const [query, setQuery] = useSearchParams();
  const days = Number(query.get('days') || 30);
  const surface = query.get('surface') || 'all';

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(['sessions', 'interactions']);
  const [definitionsOpen, setDefinitionsOpen] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try {
      const res = await client.service('admin/analytics-summary').find({ query: { days, surface } });
      setData(res);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load the numbers');
    }
  }, [days, surface]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setShown((current) => (
    current.includes(key)
      ? (current.length > 1 ? current.filter((k) => k !== key) : current)
      : [...current, key]
  ));

  const chartData = useMemo(() => (data?.series || []).map((row) => ({
    ...row,
    day: typeof row.day === 'string' ? row.day : new Date(row.day).toISOString().slice(0, 10),
  })), [data]);

  const metrics = data?.metrics || {};
  const totals = data?.totals || {};

  return (
    <Page title="Analytics">
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Analytics' }]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-3">Overview</h1>
        <p className="max-w-3xl text-sm text-gray-500">
          How much, and whether it is growing. Every figure carries what it
          counts and what it leaves out, because these are the numbers we will
          end up quoting.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setQuery({ days: String(r.days), surface })}
              className={`rounded px-3 py-1 text-sm ${
                days === r.days
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {SURFACES.map((sf) => (
              <button
                key={sf.key}
                type="button"
                onClick={() => setQuery({ days: String(days), surface: sf.key })}
                className={`rounded px-3 py-1 text-sm ${
                  surface === sf.key
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'
                }`}
              >
                {sf.label}
              </button>
            ))}
          </div>
          <Link to="/admin/analytics/visits" className="text-sm text-blue-700 underline">
            See individual visits
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}
      {!data && !error && <div className="p-6 text-sm text-gray-500">Loading...</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {HEADLINE.map((key) => (
              <MetricCard
                key={key}
                metricKey={key}
                meta={metrics[key]}
                value={totals[key]}
                change={data.comparison?.change?.[key]}
                selected={shown.includes(key)}
                onToggle={toggle}
              />
            ))}
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
              <span>Click a figure above to add or remove it from the chart.</span>
              {/* The percentages cover a different window from the totals
                  above them, so the window is stated rather than assumed. */}
              {data.comparison && (
                <span>
                  {'Percentages compare the last '}
                  {data.comparison.days}
                  {' complete days with the '}
                  {data.comparison.days}
                  {' before them. Today is in the chart but not in the comparison.'}
                </span>
              )}
            </div>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(d) => format(new Date(d), days > 90 ? 'MMM' : 'MMM d')}
                    minTickGap={24}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(d) => format(new Date(d), 'EEEE d MMMM yyyy')}
                    formatter={(v, name) => [number(v), metrics[name]?.label || name]}
                  />
                  <Legend formatter={(name) => metrics[name]?.label || name} />
                  {/* Today is not a finished day. It is shown because people
                      want to see it, and marked because a part-day plotted
                      like a whole one reads as a fall that has not happened. */}
                  {data.todayIsPartial && chartData.length > 1 && (
                    <ReferenceLine
                      x={chartData[chartData.length - 1].day}
                      stroke="#9ca3af"
                      strokeDasharray="3 3"
                      label={{
                        value: 'today, so far',
                        position: 'insideTopRight',
                        fontSize: 10,
                        fill: '#6b7280',
                      }}
                    />
                  )}
                  {shown.map((key) => (
                    <Line
                      key={key}
                      // Straight segments between measured days, not curves.
                      // A monotone curve draws a shape between two daily
                      // counts that nobody measured, and overshoots around a
                      // spike, which is the same sin as joining across a
                      // missing day. The zero-filling above exists precisely
                      // so the line makes no claim we cannot support.
                      type="linear"
                      dataKey={key}
                      stroke={SERIES_COLORS[key] || '#374151'}
                      strokeWidth={2}
                      dot={false}
                      // A day with nothing is a zero from the API rather than a
                      // gap, so the line tells the truth about a quiet day
                      // instead of drawing straight over it.
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Deliberately below the fold of the people numbers, and never in
              the same grid. It is the largest figure here and the one that
              says least about anybody. */}
          {metrics.impressions && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {metrics.impressions.label}
                </span>
                <span className="text-xl font-semibold text-gray-700">
                  {number(totals.impressions)}
                </span>
                <Change value={data.comparison?.change?.impressions} />
                <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                  not a measure of people
                </span>
              </div>
              <p className="mt-1 max-w-3xl text-xs leading-snug text-gray-600">
                {metrics.impressions.caveat}
              </p>
            </div>
          )}

          <div className="mt-4 rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => setDefinitionsOpen((v) => !v)}
              className="w-full px-4 py-3 text-left text-sm font-medium text-gray-900"
            >
              {definitionsOpen ? 'Hide' : 'What these numbers mean'}
            </button>
            {definitionsOpen && (
              <dl className="border-t border-gray-100 px-4 py-3">
                {Object.entries(metrics).map(([key, m]) => (
                  <div key={key} className="border-b border-gray-50 py-3 last:border-0">
                    <dt className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      {m.label}
                      {!m.publishable && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-800">
                          needs explaining if quoted
                        </span>
                      )}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-700">{m.means}</dd>
                    <dd className="mt-1 text-sm text-gray-500">{m.caveat}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </>
      )}
    </Page>
  );
}
